/**
 * MarkdownLiveEditor — single-pane markdown editing for a Phase 0 artifact.
 *
 * Replaces `InlineEditor`, which showed a raw `<textarea>` beside a separately rendered HTML
 * preview. Two panes meant the user read their document in one place and edited it in another, and
 * the preview pane required a markdown→HTML renderer whose output could drift from the source.
 * This component has ONE pane: the markdown source itself, visually formatted as it is typed.
 * Headings are drawn larger, `**bold**` is drawn bold, fenced blocks are drawn monospace — while
 * the document remains the exact markdown string that gets saved. There is no second
 * representation and therefore no conversion and no round-trip. See
 * `components/editor/markdownLiveExtensions.ts` for how the painting works.
 *
 * ── UNCHANGED FROM `InlineEditor` ─────────────────────────────────────────────────────────────
 * The props, the save contract and the locking rules are identical, so `FileContentView` swapped
 * one for the other without any other change:
 *   • autosave on a 3-second debounce after the last keystroke, plus an explicit "Save Draft"
 *   • hard lock while a commit is pending approval (with the explanatory banner)
 *   • word / character count, and the saving → saved indicator
 *   • `onSaved(updated)` fires after a successful PATCH — `FileContentView` uses it to refetch and,
 *     for branded artifacts, to re-run the branded pipeline
 *
 * ── CHANGED 2026-10-03 (P0 fix batch, Item 4 — F-1 resolved as option (a)) ────────────────────
 * The soft lock on a committed artifact REMAINS, but it is no longer a bare "Edit Again" button
 * with no explanation. That unexplained control is the reported defect 4b: "edit mode is shown but
 * editing doesn't work with no clear explanation". It is now `Start new version`, behind a banner
 * naming the approver and the GitHub SHA, and the lock COMMUNICATES the new-version semantics:
 * editing starts a new version, the committed version stays in the project's history unchanged, and
 * the new version needs its own approval.
 *
 * `approved_by` and the SHA come from `useArtifactVersions` row 1, not from the artifact GET — see
 * AD-9a. `get-artifact.ts` attaches commit-request data only for `status = 'pending'` rows, and
 * `approved_by` lives on the APPROVED row; `edited_by` is the last EDITOR, and attributing a
 * sign-off to the wrong person on an approval control is a correctness defect, not a cosmetic one.
 *
 * ── LOST-UPDATE GUARD ─────────────────────────────────────────────────────────────────────────
 * The local document used to be seeded from `artifact.content` ONCE and never resynced, so after a
 * chat-accepted edit or someone else's save, autosave PATCHed the stale text over the newer copy.
 * Now (see `reconcile`):
 *   • server copy changes, no unsaved local edit → the editor resyncs to it;
 *   • server copy changes WITH an unsaved local edit → autosave is cancelled and a conflict banner
 *     offers "Load latest" (adopt server, discard local) or "Keep my version" (explicit overwrite);
 *   • every save sends `expected_updated_at` (the version the edit is based on), so the server
 *     refuses a stale save with 409 ARTIFACT_STALE — which also enters the conflict state. "Keep my
 *     version" is the only save sent without it.
 *   • unmounting IN CONFLICT does not flush: leaving must not silently pick "Keep my version".
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useArtifactVersions } from "@/hooks/useArtifacts";
import { MarkdownLiveField } from "@/components/editor/MarkdownLiveField";
import {
  artifactQueryKey,
  isArtifactStale,
  usePatchArtifact,
  type ArtifactResponse,
} from "@/hooks/useArtifacts";

export interface MarkdownLiveEditorProps {
  artifact: ArtifactResponse;
  projectId: string;
  artifactType: string;
  onSaved: (updated: ArtifactResponse) => void;
}

type SaveState = "idle" | "dirty" | "saving" | "saved";

/** A server copy of the document: its content and the `updated_at` that identifies it. */
interface ServerVersion {
  content: string;
  updatedAt: string;
}

/**
 * The document changed on the server while this editor held an unsaved edit. `server` is the newer
 * copy, or null while it is still being fetched (a save was refused with 409 ARTIFACT_STALE and the
 * refetch has not landed yet).
 */
interface ConflictState {
  server: ServerVersion | null;
}

const AUTOSAVE_DELAY_MS = 3000;

/** Word count for the current content. */
function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}

export function MarkdownLiveEditor({
  artifact,
  projectId,
  artifactType,
  onSaved,
}: MarkdownLiveEditorProps): JSX.Element {
  const { mutateAsync: patchArtifact } = usePatchArtifact(
    projectId,
    artifactType,
  );

  /**
   * Version history, for the committed banner's approver + SHA (AD-9a).
   *
   * `enabled` is gated on the status so the five page-level artifact queries do not each drag a
   * versions query along with them — this runs once, for one artifact, only when a file tab is open
   * on a committed one.
   */
  const { data: versions } = useArtifactVersions(
    projectId,
    artifactType,
    artifact.status === "committed",
  );
  // Row 1 IS the current committed version, by the same `resolved_at DESC` ordering that
  // `artifact_edits.base_commit_sha` points at.
  const latestVersion = versions?.[0];
  // Prefer the version row's SHA, falling back to `base_commit_sha` — the two are maintained by the
  // same transaction in `approve-commit-request.ts`, so they agree; the fallback only matters while
  // the versions query is still pending or for pre-`artifact_commit_requests` data.
  const sha = latestVersion?.github_commit_sha ?? artifact.base_commit_sha;
  const shortSha = sha ? sha.slice(0, 7) : null;

  const queryClient = useQueryClient();

  const [content, setContent] = useState<string>(artifact.content);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [editAgain, setEditAgain] = useState<boolean>(false);
  const [conflict, setConflictState] = useState<ConflictState | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The edit that is waiting on the debounce, or null when nothing is unsaved. Held in a ref so the
   * unmount cleanup (which runs with stale closures) can still see and flush it — clearing the timer
   * alone silently discarded up to 3s of typing on every tab switch.
   */
  const pendingRef = useRef<string | null>(null);
  const patchRef = useRef(patchArtifact);
  const onSavedRef = useRef(onSaved);
  patchRef.current = patchArtifact;
  onSavedRef.current = onSaved;

  /** Latest local document — read by `reconcile`, which runs outside render. */
  const contentRef = useRef<string>(artifact.content);
  /**
   * The SERVER version the local document is based on. Sent as `expected_updated_at` on every
   * save so the server can refuse (409 ARTIFACT_STALE) a save built on an outdated copy; advanced
   * from each successful PATCH response.
   */
  const baseRef = useRef<ServerVersion>({
    content: artifact.content,
    updatedAt: artifact.updated_at,
  });
  /** Latest `artifact` prop, for `reconcile` (called from async continuations). */
  const artifactRef = useRef(artifact);
  artifactRef.current = artifact;
  /** A PATCH is in flight — `reconcile` defers until it settles (it may itself move the base). */
  const savingRef = useRef(false);
  const conflictRef = useRef<ConflictState | null>(null);
  const setConflict = useCallback((next: ConflictState | null): void => {
    conflictRef.current = next;
    setConflictState(next);
  }, []);

  // Editing is hard-locked while a commit is pending approval, and soft-locked (behind
  // "Start new version") once committed until the user opts back in.
  //
  // The non-obvious part, and the reason this needs a comment: opting in is CLIENT-ONLY. No request
  // is sent — `setEditAgain(true)` just unlocks the field. The artifact stays `committed` in the
  // database until the first actual save, which is what moves it to `draft_edit`. So an accidental
  // click costs nothing and changes nothing server-side (§5.4a).
  const pendingLock = artifact.status === "pending_commit";
  const committedLock = artifact.status === "committed" && !editAgain;
  const isReadOnly = pendingLock || committedLock;

  const clearTimer = useCallback((): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Assigned below; `save` needs to re-run reconciliation after it settles, and `reconcile` needs
  // `scheduleAutosave`, so the cycle is broken through a ref.
  const reconcileRef = useRef<() => void>(() => undefined);

  const save = useCallback(
    async (
      value: string,
      opts: { overwrite?: boolean } = {},
    ): Promise<void> => {
      pendingRef.current = null;
      savingRef.current = true;
      setSaveState("saving");
      try {
        const updated = await patchArtifact({
          content: value,
          // "Keep my version" is the ONLY unconditional write; every other save is a
          // compare-and-set against the version this editor loaded.
          expectedUpdatedAt: opts.overwrite ? null : baseRef.current.updatedAt,
        });
        baseRef.current = {
          content: updated.content,
          updatedAt: updated.updated_at,
        };
        savingRef.current = false;
        if (opts.overwrite) setConflict(null);
        // Typing during the save left a newer pending edit — it is still unsaved.
        setSaveState(pendingRef.current !== null ? "dirty" : "saved");
        onSaved(updated);
      } catch (err) {
        savingRef.current = false;
        // Keep the edit marked unsaved so it is retried on the next save / unmount instead of
        // looking safely persisted. Never clobber a NEWER edit typed while this save was in flight.
        if (pendingRef.current === null) pendingRef.current = value;
        setSaveState("dirty");
        if (isArtifactStale(err)) {
          // The server refused: the document changed under us. Stop autosaving, show the conflict
          // banner (latest version still loading), and refetch so the parent hands us the latest.
          clearTimer();
          setConflict({ server: null });
          void queryClient.invalidateQueries({
            queryKey: artifactQueryKey(projectId, artifactType),
          });
        }
        // Any other failure: the axios interceptor surfaces a toast.
      }
      // The server copy may have changed while the PATCH was in flight.
      reconcileRef.current();
    },
    [
      patchArtifact,
      onSaved,
      clearTimer,
      setConflict,
      queryClient,
      projectId,
      artifactType,
    ],
  );

  const scheduleAutosave = useCallback(
    (value: string): void => {
      clearTimer();
      pendingRef.current = value;
      timerRef.current = setTimeout(() => {
        void save(value);
      }, AUTOSAVE_DELAY_MS);
    },
    [clearTimer, save],
  );

  /**
   * Bring the editor in line with the latest SERVER copy (`artifact` prop) — run whenever that copy
   * changes (chat-accepted proposal, refetch after someone else's save, withdraw) and after every
   * save settles.
   *
   *   • same version, or an OLDER snapshot (a refetch that started before our own save landed) →
   *     nothing to do.
   *   • same content, new timestamp (status-only bump, e.g. withdraw) → just advance the base; if
   *     that is what tripped a 409, there is nothing to choose between, so drop the conflict and
   *     re-queue the local edit against the new base.
   *   • server content equals the local document → nothing to merge; adopt it as the base.
   *   • no unsaved local edit → RESYNC: replace the local document with the server copy.
   *   • unsaved local edit → CONFLICT: cancel autosave, never save automatically, ask the user.
   */
  const reconcile = useCallback((): void => {
    if (savingRef.current) return;
    const server = artifactRef.current;
    const base = baseRef.current;
    const serverTs = Date.parse(server.updated_at);
    const baseTs = Date.parse(base.updatedAt);
    if (Number.isFinite(serverTs) && Number.isFinite(baseTs)) {
      if (serverTs <= baseTs) return;
    } else if (
      server.updated_at === base.updatedAt &&
      server.content === base.content
    ) {
      return;
    }

    const latest: ServerVersion = {
      content: server.content,
      updatedAt: server.updated_at,
    };

    if (server.content === base.content) {
      baseRef.current = latest;
      if (conflictRef.current) {
        setConflict(null);
        if (pendingRef.current !== null) scheduleAutosave(pendingRef.current);
      }
      return;
    }

    if (server.content === contentRef.current) {
      baseRef.current = latest;
      clearTimer();
      pendingRef.current = null;
      setConflict(null);
      setSaveState((s) => (s === "dirty" ? "idle" : s));
      return;
    }

    if (pendingRef.current === null && !conflictRef.current) {
      baseRef.current = latest;
      contentRef.current = server.content;
      setContent(server.content);
      return;
    }

    clearTimer();
    setConflict({ server: latest });
  }, [clearTimer, scheduleAutosave, setConflict]);
  reconcileRef.current = reconcile;

  useEffect(() => {
    reconcile();
  }, [artifact.content, artifact.updated_at, reconcile]);

  // On unmount (tab switch, file close, view-mode change): FLUSH a pending edit instead of
  // dropping it. Fired without touching this component's state (it is unmounting); usePatchArtifact
  // still invalidates the artifact query, and onSaved lets the parent refresh / re-render branded.
  //
  // The flush is a compare-and-set like every other save, so if the document changed meanwhile the
  // server refuses it (409) rather than clobbering the newer copy. And IN CONFLICT it is skipped
  // entirely: the user has not chosen "Keep my version", so leaving silently must not decide for
  // them — the server copy stays, the unsaved local edit is discarded. (Nothing is shown: the
  // component is gone. The beforeunload guard below still covers a full page exit.)
  useEffect(
    () => () => {
      clearTimer();
      const pending = pendingRef.current;
      if (pending === null || conflictRef.current) return;
      pendingRef.current = null;
      void patchRef
        .current({
          content: pending,
          expectedUpdatedAt: baseRef.current.updatedAt,
        })
        .then((updated) => onSavedRef.current(updated))
        .catch(() => undefined); // the axios interceptor already surfaced the failure toast
    },
    [clearTimer],
  );

  // Leaving the page (reload / close / external navigation) cannot run an async save reliably, so
  // ask the browser to confirm while an edit is unsaved or still in flight.
  useEffect(() => {
    if (saveState !== "dirty" && saveState !== "saving") return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveState]);

  const handleChange = useCallback(
    (value: string): void => {
      // The field echoes programmatic document replacements (a resync / "Load latest") back
      // through onChange. That is not a user edit and must not be marked dirty or autosaved.
      if (value === contentRef.current) return;
      contentRef.current = value;
      setContent(value);
      if (isReadOnly) return;
      setSaveState("dirty");
      if (conflictRef.current) {
        // Keep tracking the edit, but never autosave over a document that changed elsewhere —
        // the user resolves the conflict explicitly.
        pendingRef.current = value;
        return;
      }
      scheduleAutosave(value);
    },
    [isReadOnly, scheduleAutosave],
  );

  const handleSaveDraft = (): void => {
    clearTimer();
    void save(contentRef.current);
  };

  /** Conflict → "Load latest": adopt the server copy, discarding the unsaved local edit. */
  const handleLoadLatest = (): void => {
    const server = conflictRef.current?.server;
    if (!server) return;
    clearTimer();
    pendingRef.current = null;
    baseRef.current = server;
    contentRef.current = server.content;
    setContent(server.content);
    setConflict(null);
    setSaveState("idle");
  };

  /** Conflict → "Keep my version": explicit overwrite (no expected_updated_at). */
  const handleKeepMine = (): void => {
    clearTimer();
    void save(contentRef.current, { overwrite: true });
  };

  const words = countWords(content);
  const chars = content.length;

  return (
    <div className="space-y-3">
      {pendingLock && (
        <Alert>
          <AlertDescription>
            A commit is pending approval. Withdraw it to resume editing.
          </AlertDescription>
        </Alert>
      )}

      {/* Item 4 (§5.4a) — the committed banner. Replaces a bare, unexplained `Edit Again` button.
          "Committed" in this system means a human OTHER THAN THE AUTHOR approved this content and it
          was written to the project's GitHub repository under a specific commit SHA. That
          significance is the whole premise of F-1's resolution, and the user has to be told it. */}
      {committedLock && (
        <Alert>
          <AlertDescription>
            <strong>This version is committed.</strong>{" "}
            {latestVersion?.approved_by
              ? `Approved by ${latestVersion.approved_by} and saved to GitHub`
              : "Saved to GitHub"}
            {shortSha ? ` as ${shortSha}` : ""}. Editing starts a{" "}
            <strong>new version</strong> — the committed version stays in the
            project&apos;s history and is unchanged. The new version needs its
            own approval before it is committed.
          </AlertDescription>
        </Alert>
      )}

      {/* Lost-update guard — the document changed elsewhere while an edit was unsaved. Nothing is
          saved automatically until the user picks one of the two explicit outcomes. */}
      {conflict && (
        <Alert data-testid="artifact-conflict-banner">
          <AlertDescription className="space-y-2">
            <p>
              <strong>
                This document was updated elsewhere (e.g. an accepted chat
                edit).
              </strong>{" "}
              <strong>Load latest</strong> discards your unsaved changes;{" "}
              <strong>Keep my version</strong> saves yours over it.
            </p>
            {!conflict.server && (
              <p className="text-xs text-text-secondary">
                Loading the latest version…
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleLoadLatest}
                disabled={!conflict.server || saveState === "saving"}
              >
                Load latest
              </Button>
              <Button
                size="sm"
                onClick={handleKeepMine}
                disabled={saveState === "saving"}
              >
                Keep my version
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <MarkdownLiveField
        value={content}
        onChange={handleChange}
        readOnly={isReadOnly}
        ariaLabel="Artifact markdown editor"
        className="min-h-[420px]"
      />

      {/* Footer — word count, save indicator, actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-text-secondary">
          {words} words · {chars} characters
        </span>

        <div className="flex items-center gap-3">
          {saveState === "dirty" && (
            <span className="text-xs text-text-secondary" role="status">
              Unsaved changes
            </span>
          )}
          {saveState === "saving" && (
            <span
              className="flex items-center gap-1 text-xs text-text-secondary"
              role="status"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Saving…
            </span>
          )}
          {saveState === "saved" && (
            <span
              className="flex items-center gap-1 text-xs text-success"
              role="status"
            >
              <Check className="h-3 w-3" aria-hidden="true" />
              Saved
            </span>
          )}

          {committedLock ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditAgain(true)}
            >
              Start new version
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSaveDraft}
              disabled={
                isReadOnly || saveState === "saving" || conflict !== null
              }
            >
              Save Draft
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MarkdownLiveEditor;
